import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import {
  CallForAppointmentReminder,
} from 'hooks/useApi';
import {
  getApiCommunicationGetConsentFormCommuniqueByTokenToken,
} from 'artifacts/endpoints';
import useCommuniqueState from 'hooks/useCommuniqueState';
import useIntakeFlags from 'hooks/useIntakeFlags';
import useIncompleteRtmRedirect from 'hooks/useIncompleteRtmRedirect';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';
import { appStateSettingsAction, AppState } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import {
  isEmptyString,
  parseErrorMessage,
} from 'utils/helper';
import { getRtmCardCopy, RtmKind } from 'utils/rtmChoice';

export type ChoiceStep = {
  id: 'intake' | 'tempo';
  title: string;
  subtitle: string;
};

type UseChoiceScreenProps = {
  onAiCallSuccess: (doneNote: string) => void;
};

const aiCallDoneNote = 'An agent will call you shortly to collect your intake information.';

function getChoiceSteps(
  isTempoEnabledForOrg: boolean,
): ChoiceStep[] {
  return [
    {
      id: 'intake',
      title: 'Your intake',
      subtitle: 'Pick one • about 5 min',
    },
    ...(isTempoEnabledForOrg
      ? [{
        id: 'tempo' as const,
        title: 'Psych Screening',
        subtitle: 'Questionnaire • about 3 min',
      }]
      : []),
  ];
}

export default function useChoiceScreen({
  onAiCallSuccess,
}: UseChoiceScreenProps) {
  const dispatch = useDispatch();
  const { communiqueData } = useCommuniqueState();

  // --- Shared flag fetching (single source of truth) ---
  const {
    flags: sharedFlags,
    pccItems: sharedPccItems,
    isLoading: isLoadingSharedFlags,
  } = useIntakeFlags();

  // Pass pre-fetched flags to both sub-hooks so they skip their own fetching
  const { startTempo: defaultStartTempo } = useIncompleteTempoRedirect(sharedFlags, sharedPccItems);
  const {
    isLoadingRtm,
    isRtmComplete: defaultIsRtmComplete,
    rtmCardCopy: defaultRtmCardCopy,
    shouldShowRtmCard,
    startRtm: defaultStartRtm,
  } = useIncompleteRtmRedirect(sharedFlags, sharedPccItems);

  const {
    mutateAsync: callForAppointmentReminder,
    isPending: isCalling,
  } = CallForAppointmentReminder();
  const [callErrorMessage, setCallErrorMessage] = useState('');
  const [loadErrorMessage, setLoadErrorMessage] = useState('');
  const [isStartingConsent, setIsStartingConsent] = useState(false);

  const { appointmentToken, appointmentPatientId } = useSelector(
    (state: RootState) => state.appStateSettings,
  );
  const token = appointmentToken || communiqueData?.token;
  const patientId = appointmentPatientId || communiqueData?.patientId;
  const appointmentId = communiqueData?.appointmentId ?? 0;

  // Use shared flags directly
  const intakeFlags = sharedFlags;
  const pccItems = sharedPccItems;

  const {
    shouldShowIntake = false,
    shouldShowTempo = false,
    isTempoEnabledForOrg = false,
    isPCCEnabledForOrg = false,
  } = intakeFlags ?? {};

  const rtmKindNormalized: RtmKind | null = useMemo(() => {
    if (isPCCEnabledForOrg && pccItems?.rtm?.kind) {
      const k = pccItems.rtm.kind.toString().toLowerCase();
      return k.includes('checkin') ? 'checkIn' : 'enrollment';
    }
    return null;
  }, [isPCCEnabledForOrg, pccItems?.rtm?.kind]);

  const rtmCardCopy = useMemo(() => {
    if (rtmKindNormalized) {
      return getRtmCardCopy(rtmKindNormalized);
    }
    return defaultRtmCardCopy;
  }, [defaultRtmCardCopy, rtmKindNormalized]);

  const isConsentPending = Boolean(
    isPCCEnabledForOrg
      && !isEmptyString(pccItems?.consent?.token)
      && pccItems?.consent?.status !== 'Completed',
  );

  const hasTempoCard = isPCCEnabledForOrg
    ? Boolean(pccItems?.tempo?.isEnabled !== false && !isEmptyString(pccItems?.tempo?.token))
    : (isTempoEnabledForOrg && Boolean(getChoiceSteps(isTempoEnabledForOrg).find((s) => s.id === 'tempo')));
  const isTempoComplete = isPCCEnabledForOrg
    ? (pccItems?.tempo?.status === 'Completed')
    : !shouldShowTempo;

  const hasRtmCard = isPCCEnabledForOrg
    ? Boolean(pccItems?.rtm?.isEnabled !== false && !isEmptyString(pccItems?.rtm?.token))
    : shouldShowRtmCard;
  const isRtmCompleteResolved = isPCCEnabledForOrg
    ? (pccItems?.rtm?.status === 'Completed')
    : defaultIsRtmComplete;

  const choiceSteps = useMemo(
    () => getChoiceSteps(isTempoEnabledForOrg || hasTempoCard),
    [hasTempoCard, isTempoEnabledForOrg],
  );

  const intakeStep = choiceSteps.find((step) => step.id === 'intake') ?? null;
  const tempoStep = choiceSteps.find((step) => step.id === 'tempo') ?? null;

  const completedOptionalSteps = Number(!shouldShowIntake)
    + Number(hasTempoCard && isTempoComplete)
    + Number(hasRtmCard && isRtmCompleteResolved);
  const remainingActionableSteps = Number(shouldShowIntake)
    + Number(hasTempoCard && !isTempoComplete)
    + Number(hasRtmCard && !isRtmCompleteResolved);
  const isAllStepsComplete = remainingActionableSteps === 0;

  const totalSteps = 1
    + Number(Boolean(intakeStep))
    + Number(hasTempoCard)
    + Number(hasRtmCard);
  const currentStepNumber = Math.min(2 + completedOptionalSteps, totalSteps);

  let remainingStepsLabel = 'All steps complete';
  let finishLaterLabel = "I'm done";

  remainingStepsLabel = remainingActionableSteps === 1
    ? 'One quick step left'
    : remainingStepsLabel;
  finishLaterLabel = remainingActionableSteps === 1
    ? "I'll finish this later"
    : finishLaterLabel;

  remainingStepsLabel = remainingActionableSteps === 2
    ? 'Two quick steps left'
    : remainingStepsLabel;
  finishLaterLabel = remainingActionableSteps === 2
    ? "I'll finish these later"
    : finishLaterLabel;

  remainingStepsLabel = remainingActionableSteps >= 3
    ? 'A few quick steps left'
    : remainingStepsLabel;
  finishLaterLabel = remainingActionableSteps >= 3
    ? "I'll finish these later"
    : finishLaterLabel;

  const errorMessage = callErrorMessage || loadErrorMessage;
  const isLoading = isLoadingSharedFlags
    || isLoadingRtm;

  const triggerConsentForm = useCallback(async (targetModule?: AppState) => {
    const consentToken = pccItems?.consent?.token;
    if (!isEmptyString(consentToken)) {
      setIsStartingConsent(true);
      setCallErrorMessage('');
      try {
        const consentData = await getApiCommunicationGetConsentFormCommuniqueByTokenToken(
          consentToken ?? '',
        );
        dispatch(appStateSettingsAction({
          name: ModuleTypes.consentForm,
          page: null,
          pccConsentWorkflowId: consentData?.formWorkflowId ?? null,
          pendingTarget: targetModule ?? null,
        }));
      } catch (err) {
        setCallErrorMessage(parseErrorMessage(err, 'Unable to load consent form. Please try again.'));
      } finally {
        setIsStartingConsent(false);
      }
    }
  }, [
    dispatch,
    pccItems?.consent?.token,
  ]);

  const handleStartIntake = useCallback(() => {
    const target: AppState = { name: ModuleTypes.patientList, page: null };
    dispatch(appStateSettingsAction(target));
  }, [dispatch]);

  const handleStartTempo = useCallback(() => {
    const target: AppState = isPCCEnabledForOrg && !isEmptyString(pccItems?.tempo?.token)
      ? { name: ModuleTypes.tempo, page: null, tempoToken: pccItems?.tempo?.token ?? '' }
      : { name: ModuleTypes.tempo, page: null };

    if (isConsentPending) {
      triggerConsentForm(target);
    } else if (isPCCEnabledForOrg && !isEmptyString(pccItems?.tempo?.token)) {
      dispatch(appStateSettingsAction(target));
    } else {
      defaultStartTempo();
    }
  }, [
    defaultStartTempo,
    dispatch,
    isConsentPending,
    isPCCEnabledForOrg,
    pccItems?.tempo?.token,
    triggerConsentForm,
  ]);

  const handleStartRtm = useCallback(() => {
    const isEnrollment = (pccItems?.rtm?.kind?.toString() ?? '').toLowerCase().includes('enrollment');
    let rtmKind: RtmKind | null = null;
    if (isPCCEnabledForOrg && pccItems?.rtm?.kind) {
      rtmKind = isEnrollment ? 'enrollment' : 'checkIn';
    }
    const target: AppState | null = isPCCEnabledForOrg && !isEmptyString(pccItems?.rtm?.token)
      ? {
        name: isEnrollment ? ModuleTypes.rtmEnrollment : ModuleTypes.rtmCheckIn,
        page: null,
        rtmToken: pccItems?.rtm?.token ?? '',
        rtmKind,
      }
      : null;

    if (isConsentPending) {
      if (target) {
        triggerConsentForm(target);
      } else {
        triggerConsentForm();
      }
    } else if (target) {
      dispatch(appStateSettingsAction(target));
    } else {
      defaultStartRtm();
    }
  }, [
    defaultStartRtm,
    dispatch,
    isConsentPending,
    isPCCEnabledForOrg,
    pccItems?.rtm,
    triggerConsentForm,
  ]);

  const handleCallMe = useCallback(async () => {
    try {
      setCallErrorMessage('');
      await callForAppointmentReminder({
        encounterId: appointmentId,
        skipAppointmentConfirmation: true,
      });
      onAiCallSuccess(aiCallDoneNote);
    } catch (error) {
      setCallErrorMessage(parseErrorMessage(error));
    }
  }, [appointmentId, callForAppointmentReminder, onAiCallSuccess]);

  const intakeButtonLabel = 'Fill it out';
  const tempoStartLabel = isConsentPending ? 'Sign consent and Start' : 'Start screening';
  const rtmStartLabel = isConsentPending ? 'Sign consent and Start' : (rtmCardCopy?.startLabel ?? 'Start');

  return {
    errorMessage,
    finishLaterLabel,
    handleCallMe,
    handleStartIntake,
    hasRtmCard,
    hasTempoCard,
    intakeButtonLabel,
    intakeStep,
    isCalling,
    isConsentPending,
    isAllStepsComplete,
    isLoading,
    isPCCEnabledForOrg,
    pccItems,
    isRtmComplete: isRtmCompleteResolved,
    isStartingConsent,
    isTempoComplete,
    isTempoEnabledForOrg,
    remainingStepsLabel,
    rtmCardCopy,
    rtmStartLabel,
    shouldShowIntake,
    shouldShowRtmCard: hasRtmCard,
    shouldShowTempo,
    startRtm: handleStartRtm,
    startTempo: handleStartTempo,
    tempoStartLabel,
    tempoStep,
    totalSteps,
    currentStepNumber,
  };
}
